import React, { useState, useRef, useEffect } from 'react';
import { Box, Grid, Typography, CircularProgress, IconButton, LinearProgress, Paper, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Modal from '@Components/Modal';
import SbButton from '@Components/Button';
import { AccountAiFields, processAccountDocumentExtraction } from './accountExtractionService';

interface ImportAccountAiModalProps {
  open: boolean;
  onClose: () => void;
  onExtracted: (fields: AccountAiFields, confidence?: number, invalidFields?: string[]) => void;
}

export function ImportAccountAiModal({ open, onClose, onExtracted }: ImportAccountAiModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isCancelledRef = useRef<boolean>(false);

  // Clean up any pending polling timer on unmount
  useEffect(() => {
    return () => {
      isCancelledRef.current = true;
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    };
  }, []);

  // Reset state on modal open/close
  useEffect(() => {
    if (open) {
      isCancelledRef.current = false;
      setSelectedFile(null);
      setIsProcessing(false);
      setStatusMessage('');
      setUploadProgress(0);
      setErrorMessage(null);
      setIsDragOver(false);
    } else {
      isCancelledRef.current = true;
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    }
  }, [open]);

  const handleCancelAndClose = () => {
    isCancelledRef.current = true;
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
    setIsProcessing(false);
    onClose();
  };

  const handleFileSelect = (file: File) => {
    setErrorMessage(null);
    setSelectedFile(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (isProcessing) return;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!isProcessing) setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleExtract = async () => {
    if (!selectedFile) {
      setErrorMessage('Please select a document file first.');
      return;
    }
    if (isProcessing) {
      return;
    }

    isCancelledRef.current = false;
    setIsProcessing(true);
    setErrorMessage(null);
    setUploadProgress(0);

    try {
      const response = await processAccountDocumentExtraction(selectedFile, {
        onStatusChange: msg => {
          if (!isCancelledRef.current) setStatusMessage(msg);
        },
        onUploadProgress: percent => {
          if (!isCancelledRef.current) setUploadProgress(percent);
        },
        isCancelled: () => isCancelledRef.current,
        registerTimer: timer => {
          pollingTimerRef.current = timer;
        },
      });

      if (isCancelledRef.current) return;

      if (response.status === 'completed') {
        if (response.fields && Object.keys(response.fields).length > 0) {
          onExtracted(response.fields, response.confidence, response.invalidFields);
          onClose();
        } else {
          setErrorMessage('No extractable fields returned from document.');
        }
      } else if (response.status === 'needs_review') {
        const invalidFieldsMsg =
          Array.isArray(response.invalidFields) && response.invalidFields.length > 0
            ? ` (${response.invalidFields.join(', ')})`
            : '';
        setErrorMessage(
          response.error || `Document extraction requires review${invalidFieldsMsg}. Please enter details manually.`,
        );
      } else if (response.status === 'failed' || response.error) {
        setErrorMessage(response.error || 'Failed to extract document details. Please try again.');
      } else {
        setErrorMessage(response.error || `Unexpected extraction status: ${response.status}`);
      }
    } catch (err: any) {
      if (isCancelledRef.current) return;
      const msg =
        err?.response?.data?.msg ||
        err?.response?.data?.errorDetails ||
        err?.message ||
        'Failed to process document. Please try again.';
      setErrorMessage(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      if (!isCancelledRef.current) {
        setIsProcessing(false);
      }
      pollingTimerRef.current = null;
    }
  };

  return (
    <Modal open={open} onClose={handleCancelAndClose} maxWidth="md">
      {/* Hidden File Input */}
      <input type="file" ref={fileInputRef} onChange={handleInputChange} style={{ display: 'none' }} accept="*/*" />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon color="primary" sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" className="font-bold" sx={{ color: 'text.primary', lineHeight: 1.2 }}>
              Import with AI
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Upload any document to auto-fill account creation form fields
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleCancelAndClose} size="small" sx={{ color: 'text.secondary' }}>
          <HighlightOffIcon />
        </IconButton>
      </Box>

      {/* Error display */}
      {errorMessage && (
        <Box
          sx={(theme: any) => ({
            mb: 3,
            p: 2,
            borderRadius: '12px',
            backgroundColor: alpha(theme.palette.error.main, 0.08),
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          })}
        >
          <ErrorOutlineIcon color="error" />
          <Typography color="error" variant="body2" className="font-medium">
            {errorMessage}
          </Typography>
        </Box>
      )}

      {/* Main Content Area */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => !isProcessing && fileInputRef.current?.click()}
            sx={(theme: any) => ({
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 4,
              minHeight: '220px',
              border: '2px dashed',
              borderColor: isDragOver
                ? theme.palette.primary.main
                : selectedFile
                ? theme.palette.primary.main
                : alpha(theme.palette.primary.main, 0.3),
              borderRadius: '16px',
              backgroundColor: isDragOver
                ? alpha(theme.palette.primary.main, 0.08)
                : selectedFile
                ? alpha(theme.palette.success.main, 0.04)
                : alpha(theme.palette.primary.main, 0.02),
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease-in-out',
              opacity: isProcessing ? 0.7 : 1,
              '&:hover': {
                borderColor: isProcessing ? undefined : theme.palette.primary.main,
                backgroundColor: isProcessing ? undefined : alpha(theme.palette.primary.main, 0.05),
              },
            })}
          >
            {selectedFile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <CheckCircleIcon color="success" sx={{ fontSize: '48px', mb: 1 }} />
                <Typography variant="subtitle1" className="font-bold mb-1" sx={{ color: 'text.primary' }}>
                  {selectedFile.name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', mb: 2 }}>
                  {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB — {selectedFile.type || 'Document'}
                </Typography>
                {!isProcessing && (
                  <Typography
                    variant="caption"
                    sx={{ color: 'primary.main', fontWeight: 600, textDecoration: 'underline' }}
                  >
                    Click or drag to choose a different file
                  </Typography>
                )}
              </Box>
            ) : (
              <>
                <CloudUploadIcon color="primary" sx={{ fontSize: '52px', mb: 1.5 }} />
                <Typography variant="subtitle1" className="font-bold mb-1" sx={{ color: 'text.primary' }}>
                  Choose 1 File to Import
                </Typography>
                <Typography variant="body2" className="text-center mb-1" sx={{ color: 'text.secondary' }}>
                  Drag & drop or click to browse
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                  Supports PDF, Images, Excel, Word, or Text documents
                </Typography>
              </>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Processing indicator */}
      {isProcessing && (
        <Paper
          elevation={0}
          sx={(theme: any) => ({
            mt: 3,
            p: 2.5,
            borderRadius: '12px',
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          })}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
            <CircularProgress size={24} color="primary" />
            <Typography variant="body2" className="font-semibold" sx={{ color: 'text.primary' }}>
              {statusMessage || 'Processing document...'}
            </Typography>
          </Box>
          {uploadProgress > 0 && uploadProgress < 100 && (
            <Box sx={{ width: '100%', mt: 1 }}>
              <LinearProgress
                color="primary"
                variant="determinate"
                value={uploadProgress}
                sx={{ borderRadius: 4, height: 6 }}
              />
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block', textAlign: 'right', mt: 0.5 }}
              >
                {uploadProgress}% uploaded
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
        <SbButton variant="outlined" onClick={handleCancelAndClose} sx={{ borderRadius: '8px !important' }}>
          Cancel
        </SbButton>
        <SbButton
          variant="contained"
          color="primary"
          onClick={handleExtract}
          disabled={!selectedFile || isProcessing}
          startIcon={isProcessing ? undefined : <AutoAwesomeIcon />}
          sx={{
            borderRadius: '8px !important',
          }}
        >
          {isProcessing ? 'Extracting Details...' : 'Extract & Fill Form'}
        </SbButton>
      </Box>
    </Modal>
  );
}

export function ImportAccountAiButton({
  onExtracted,
  buttonText = 'Import with AI',
}: {
  onExtracted: (fields: AccountAiFields, confidence?: number, invalidFields?: string[]) => void;
  buttonText?: string;
}) {
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  return (
    <>
      <SbButton
        variant="outlined"
        color="primary"
        startIcon={<AutoAwesomeIcon sx={{ fontSize: '18px !important' }} />}
        onClick={() => setOpen(true)}
        sx={{
          borderRadius: '24px',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          px: 2.75,
          py: 1,
          bgcolor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText || '#fff',
          boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.25)}`,
          alignSelf: { xs: 'stretch', sm: 'center' },
          '&:hover': {
            bgcolor: theme.palette.primary.dark,
            boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.35)}`,
            transform: 'translateY(-1px)',
          },
          transition: 'all 0.2s ease-in-out',
        }}
      >
        {buttonText}
      </SbButton>
      <ImportAccountAiModal open={open} onClose={() => setOpen(false)} onExtracted={onExtracted} />
    </>
  );
}
